import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanyInventory.css";

import { usePermissions } from "./PermissionContext";
import { API_URL, getPosHeaders } from "./api";
import InventoryExcelPOS from "./InventoryExcelPOS";

const PAGE_SIZE = 10;

const ENDPOINTS = {
  inventory: "/pos/inventory/me",
  alerts: "/pos/inventory/me/alerts/summary",
  movements: "/pos/inventory/me/movements",
  lots: "/pos/inventory/me/lots",
  warehouses: "/pos/warehouses/me",
  products: "/products/pos/me",
  variants: (id) => `/pos/products/me/${id}/variants`,
};

const initialMovement = {
  warehouse_id: "",
  product_id: "",
  variant_id: "",
  movement_type: "entry",
  quantity: "",
  concept: "",
  note: "",
  lot_number: "",
  expiration_date: "",
};

const initialAlerts = {
  total_alerts: 0,
  exhausted: 0,
  critical: 0,
  low: 0,
  expired: 0,
  expiring_soon: 0,
};

function errorMessage(error, fallback) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .filter(Boolean)
      .join(". ");
  }

  if (error.response?.status === 401) {
    return "La sesión POS venció. Inicia sesión nuevamente.";
  }

  if (error.response?.status === 403) {
    return "La sesión no tiene acceso a esta operación.";
  }

  if (error.response?.status === 404) {
    return "No se encontró el recurso o la ruta solicitada.";
  }

  return fallback;
}

function getItems(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.products)) return data.products;

  return [];
}

function getPage(data) {
  const items = getItems(data);
  const total = Number(data?.total);

  return {
    items,
    total: Number.isFinite(total)
      ? Math.max(0, total)
      : items.length,
  };
}

function lastOffset(total) {
  return (
    Math.max(0, Math.ceil(total / PAGE_SIZE) - 1) *
    PAGE_SIZE
  );
}

function formatQuantity(value) {
  return new Intl.NumberFormat("es-CO", {
    maximumFractionDigits: 3,
  }).format(Number(value || 0));
}

function formatMoney(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "Sin vencimiento";

  const date = new Date(
    `${String(value).slice(0, 10)}T00:00:00`
  );

  return Number.isNaN(date.getTime())
    ? "Fecha inválida"
    : date.toLocaleDateString("es-CO");
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString("es-CO");
}

function lotStatusLabel(status) {
  const labels = {
    expired: "Vencido",
    expiring_soon: "Próximo a vencer",
    valid: "Vigente",
    without_expiration: "Sin vencimiento",
  };

  return labels[status] || "Sin estado";
}

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebounced(value);
    }, delay);

    return () => window.clearTimeout(timeout);
  }, [value, delay]);

  return debounced;
}

function Pagination({
  total,
  offset,
  onPageChange,
}) {
  const totalPages = Math.max(
    1,
    Math.ceil(total / PAGE_SIZE)
  );

  const currentPage = Math.min(
    totalPages,
    Math.floor(offset / PAGE_SIZE) + 1
  );

  const start = total
    ? (currentPage - 1) * PAGE_SIZE + 1
    : 0;

  const end = Math.min(
    currentPage * PAGE_SIZE,
    total
  );

  const pages = [
    ...new Set([
      1,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      totalPages,
    ]),
  ]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  return (
    <div className="inventory-pagination">
      <span>
        Mostrando {start}–{end} de {total} registros
      </span>

      <nav aria-label="Paginación">
        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(0)}
          aria-label="Primera página"
        >
          «
        </button>

        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() =>
            onPageChange(
              (currentPage - 2) * PAGE_SIZE
            )
          }
        >
          Anterior
        </button>

        {pages.map((page, index) => (
          <span key={page}>
            {index > 0 &&
              page - pages[index - 1] > 1 && (
                <span aria-hidden="true"> … </span>
              )}

            <button
              type="button"
              className={
                page === currentPage ? "active" : ""
              }
              aria-current={
                page === currentPage
                  ? "page"
                  : undefined
              }
              onClick={() =>
                onPageChange(
                  (page - 1) * PAGE_SIZE
                )
              }
            >
              {page}
            </button>
          </span>
        ))}

        <button
          type="button"
          disabled={currentPage >= totalPages}
          onClick={() =>
            onPageChange(
              currentPage * PAGE_SIZE
            )
          }
        >
          Siguiente
        </button>

        <button
          type="button"
          disabled={currentPage >= totalPages}
          onClick={() =>
            onPageChange(
              (totalPages - 1) * PAGE_SIZE
            )
          }
          aria-label="Última página"
        >
          »
        </button>
      </nav>
    </div>
  );
}

export default function CompanyInventory() {
  const { company } = useParams();

  const {
    can,
    loadingPermissions,
  } = usePermissions();

  const canViewInventory = can("inventory.view");

  const enabled =
    !loadingPermissions && canViewInventory;

  const [inventory, setInventory] = useState([]);
  const [inventoryTotal, setInventoryTotal] = useState(0);
  const [inventoryOffset, setInventoryOffset] = useState(0);
  const [inventorySearch, setInventorySearch] = useState("");
  const [onlyLowStock, setOnlyLowStock] = useState(false);

  const [movements, setMovements] = useState([]);
  const [movementsTotal, setMovementsTotal] = useState(0);
  const [movementsOffset, setMovementsOffset] = useState(0);
  const [movementSearch, setMovementSearch] = useState("");

  const [
    movementTypeFilter,
    setMovementTypeFilter,
  ] = useState("");

  const [
    movementProductFilter,
    setMovementProductFilter,
  ] = useState("");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [lots, setLots] = useState([]);
  const [lotsTotal, setLotsTotal] = useState(0);
  const [lotsOffset, setLotsOffset] = useState(0);

  const [
    lotExpirationFilter,
    setLotExpirationFilter,
  ] = useState("");

  const [warehouses, setWarehouses] = useState([]);

  const [
    selectedWarehouseId,
    setSelectedWarehouseId,
  ] = useState("");

  const [
    productOptions,
    setProductOptions,
  ] = useState([]);

  const [
    productVariants,
    setProductVariants,
  ] = useState([]);

  const [
    loadingProductVariants,
    setLoadingProductVariants,
  ] = useState(false);

  const [variantError, setVariantError] = useState("");

  const [
    movementData,
    setMovementData,
  ] = useState({
    ...initialMovement,
  });

  const [
    alertSummary,
    setAlertSummary,
  ] = useState({
    ...initialAlerts,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [
    refreshVersion,
    setRefreshVersion,
  ] = useState(0);

  const savingRef = useRef(false);

  const debouncedInventorySearch =
    useDebouncedValue(inventorySearch);

  const debouncedMovementSearch =
    useDebouncedValue(movementSearch);

  const request = useCallback(
    (path, params = {}, signal) =>
      axios.get(`${API_URL}${path}`, {
        headers: getPosHeaders(company),
        params,
        signal,
      }),
    [company]
  );

  // Existencias y paginación.
  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();

    setLoading(true);

    setErrors((previous) => ({
      ...previous,
      inventory: "",
    }));

    request(
      ENDPOINTS.inventory,
      {
        limit: PAGE_SIZE,
        offset: inventoryOffset,
        search:
          debouncedInventorySearch.trim() || undefined,
        only_low_stock: onlyLowStock,
        warehouse_id:
          selectedWarehouseId || undefined,
      },
      controller.signal
    )
      .then(({ data }) => {
        if (controller.signal.aborted) return;

        const page = getPage(data);

        setInventory(page.items);
        setInventoryTotal(page.total);

        if (inventoryOffset > lastOffset(page.total)) {
          setInventoryOffset(lastOffset(page.total));
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;

        setInventory([]);
        setInventoryTotal(0);

        setErrors((previous) => ({
          ...previous,
          inventory: errorMessage(
            error,
            "No fue posible cargar el inventario."
          ),
        }));
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [
    enabled,
    request,
    inventoryOffset,
    debouncedInventorySearch,
    onlyLowStock,
    selectedWarehouseId,
    refreshVersion,
  ]);

  // Opciones de bodegas y productos.
  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();

    async function loadOptions() {
      const [
        warehouseResult,
        productResult,
      ] = await Promise.allSettled([
        request(
          ENDPOINTS.warehouses,
          { active_only: true },
          controller.signal
        ),
        request(
          ENDPOINTS.products,
          {},
          controller.signal
        ),
      ]);

      if (controller.signal.aborted) return;

      setWarehouses(
        warehouseResult.status === "fulfilled"
          ? getItems(warehouseResult.value.data)
          : []
      );

      setProductOptions(
        productResult.status === "fulfilled"
          ? getItems(productResult.value.data)
          : []
      );

      setErrors((previous) => ({
        ...previous,
        warehouses:
          warehouseResult.status === "rejected"
            ? errorMessage(
                warehouseResult.reason,
                "No fue posible cargar las bodegas."
              )
            : "",
        products:
          productResult.status === "rejected"
            ? errorMessage(
                productResult.reason,
                "No fue posible cargar los productos."
              )
            : "",
      }));
    }

    loadOptions();

    return () => controller.abort();
  }, [
    enabled,
    request,
    refreshVersion,
  ]);

  // Resumen de alertas.
  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();

    request(
      ENDPOINTS.alerts,
      {
        warehouse_id:
          selectedWarehouseId || undefined,
      },
      controller.signal
    )
      .then(({ data }) => {
        if (controller.signal.aborted) return;

        setAlertSummary({
          ...initialAlerts,
          ...data,
        });

        setErrors((previous) => ({
          ...previous,
          alerts: "",
        }));
      })
      .catch((error) => {
        if (controller.signal.aborted) return;

        setAlertSummary({
          ...initialAlerts,
        });

        setErrors((previous) => ({
          ...previous,
          alerts: errorMessage(
            error,
            "No fue posible cargar las alertas."
          ),
        }));
      });

    return () => controller.abort();
  }, [
    enabled,
    request,
    selectedWarehouseId,
    refreshVersion,
  ]);

  // Historial de movimientos.
  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();

    if (
      dateFrom &&
      dateTo &&
      dateFrom > dateTo
    ) {
      setMovements([]);
      setMovementsTotal(0);

      setErrors((previous) => ({
        ...previous,
        movements:
          "La fecha inicial no puede superar la final.",
      }));

      return () => controller.abort();
    }

    request(
      ENDPOINTS.movements,
      {
        limit: PAGE_SIZE,
        offset: movementsOffset,
        search:
          debouncedMovementSearch.trim() || undefined,
        product_id:
          movementProductFilter || undefined,
        movement_type:
          movementTypeFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        warehouse_id:
          selectedWarehouseId || undefined,
      },
      controller.signal
    )
      .then(({ data }) => {
        if (controller.signal.aborted) return;

        const page = getPage(data);

        setMovements(page.items);
        setMovementsTotal(page.total);

        setErrors((previous) => ({
          ...previous,
          movements: "",
        }));

        if (movementsOffset > lastOffset(page.total)) {
          setMovementsOffset(lastOffset(page.total));
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;

        setMovements([]);
        setMovementsTotal(0);

        setErrors((previous) => ({
          ...previous,
          movements: errorMessage(
            error,
            "No fue posible cargar los movimientos."
          ),
        }));
      });

    return () => controller.abort();
  }, [
    enabled,
    request,
    movementsOffset,
    debouncedMovementSearch,
    movementProductFilter,
    movementTypeFilter,
    dateFrom,
    dateTo,
    selectedWarehouseId,
    refreshVersion,
  ]);

  // Lotes disponibles.
  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();

    request(
      ENDPOINTS.lots,
      {
        limit: PAGE_SIZE,
        offset: lotsOffset,
        expiration_status:
          lotExpirationFilter || undefined,
        warehouse_id:
          selectedWarehouseId || undefined,
      },
      controller.signal
    )
      .then(({ data }) => {
        if (controller.signal.aborted) return;

        const page = getPage(data);

        setLots(page.items);
        setLotsTotal(page.total);

        setErrors((previous) => ({
          ...previous,
          lots: "",
        }));

        if (lotsOffset > lastOffset(page.total)) {
          setLotsOffset(lastOffset(page.total));
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;

        setLots([]);
        setLotsTotal(0);

        setErrors((previous) => ({
          ...previous,
          lots: errorMessage(
            error,
            "No fue posible cargar los lotes."
          ),
        }));
      });

    return () => controller.abort();
  }, [
    enabled,
    request,
    lotsOffset,
    lotExpirationFilter,
    selectedWarehouseId,
    refreshVersion,
  ]);

  // Variantes del producto seleccionado.
  useEffect(() => {
    const productId = Number(
      movementData.product_id
    );

    setProductVariants([]);
    setVariantError("");

    if (!enabled || !productId) {
      setLoadingProductVariants(false);
      return undefined;
    }

    const controller = new AbortController();

    setLoadingProductVariants(true);

    request(
      ENDPOINTS.variants(productId),
      { only_active: true },
      controller.signal
    )
      .then(({ data }) => {
        if (!controller.signal.aborted) {
          setProductVariants(getItems(data));
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setVariantError(
            errorMessage(
              error,
              "No fue posible cargar las variantes."
            )
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingProductVariants(false);
        }
      });

    return () => controller.abort();
  }, [
    enabled,
    request,
    movementData.product_id,
  ]);

  const refreshInventory = useCallback(() => {
    setInventoryOffset(0);
    setMovementsOffset(0);
    setLotsOffset(0);

    setRefreshVersion((current) => current + 1);
  }, []);

  const handleWarehouseChange = (event) => {
    setSelectedWarehouseId(event.target.value);
    setInventoryOffset(0);
    setMovementsOffset(0);
    setLotsOffset(0);
  };

  const handleMovementChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    if (name === "product_id") {
      setProductVariants([]);
      setVariantError("");
      setLoadingProductVariants(Boolean(value));
    }

    setMovementData((current) => ({
      ...current,
      [name]: value,
      ...(name === "product_id"
        ? { variant_id: "" }
        : {}),
    }));
  };

  const showStockAlerts = () => {
    setInventorySearch("");
    setOnlyLowStock(true);
    setInventoryOffset(0);

    document
      .getElementById("inventory-stock")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  const showLotAlerts = (status) => {
    setLotExpirationFilter(status);
    setLotsOffset(0);

    document
      .getElementById("inventory-lots")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  const createMovement = async (event) => {
    event.preventDefault();

    if (savingRef.current) return;

    if (
      !movementData.warehouse_id ||
      !movementData.product_id
    ) {
      toast.error(
        "Selecciona una bodega y un producto."
      );
      return;
    }

    const quantity = Number(
      movementData.quantity
    );

    if (
      !Number.isFinite(quantity) ||
      quantity <= 0
    ) {
      toast.error(
        "La cantidad debe ser mayor que cero."
      );
      return;
    }

    const concept = movementData.concept.trim();

    if (
      concept.length < 3 ||
      concept.length > 255
    ) {
      toast.error(
        "El concepto debe tener entre 3 y 255 caracteres."
      );
      return;
    }

    if (
      loadingProductVariants ||
      variantError
    ) {
      toast.error(
        "Primero debes cargar las variantes del producto."
      );
      return;
    }

    savingRef.current = true;
    setSaving(true);

    try {
      await axios.post(
        `${API_URL}${ENDPOINTS.movements}`,
        {
          warehouse_id: Number(
            movementData.warehouse_id
          ),
          product_id: Number(
            movementData.product_id
          ),
          variant_id: movementData.variant_id
            ? Number(movementData.variant_id)
            : null,
          movement_type:
            movementData.movement_type,
          quantity,
          concept,
          note:
            movementData.note.trim() || null,
          lot_number:
            movementData.movement_type === "entry"
              ? movementData.lot_number.trim() || null
              : null,
          expiration_date:
            movementData.movement_type === "entry"
              ? movementData.expiration_date || null
              : null,
        },
        {
          headers: getPosHeaders(company),
        }
      );

      toast.success(
        movementData.movement_type === "entry"
          ? "Entrada de inventario registrada."
          : "Salida de inventario registrada."
      );

      setMovementData({
        ...initialMovement,
      });

      refreshInventory();
    } catch (error) {
      toast.error(
        errorMessage(
          error,
          "No fue posible registrar el movimiento."
        )
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const selectedProduct = productOptions.find(
    (product) =>
      Number(product.id) ===
      Number(movementData.product_id)
  );

  const selectedVariant = productVariants.find(
    (variant) =>
      Number(variant.id) ===
      Number(movementData.variant_id)
  );

  const productNames = useMemo(
    () =>
      Object.fromEntries(
        productOptions.map((product) => [
          product.id,
          product.name,
        ])
      ),
    [productOptions]
  );

  const resetMovementFilters = () => {
    setMovementSearch("");
    setMovementTypeFilter("");
    setMovementProductFilter("");
    setDateFrom("");
    setDateTo("");
    setMovementsOffset(0);
  };

  if (loadingPermissions) {
    return (
      <div role="status">
        Validando sesión POS…
      </div>
    );
  }

  if (!canViewInventory) {
    return (
      <div
        className="alert alert-warning"
        role="alert"
      >
        No tienes acceso al módulo de inventario.
      </div>
    );
  }

  return (
    <div className="company-inventory">
      <div className="inventory-heading">
        <div>
          <h1>Inventario</h1>

          <p>
            Consulta existencias, movimientos, lotes
            y alertas de tu empresa.
          </p>

          <button
            type="button"
            className="btn-clear-filters"
            disabled={saving || loading}
            onClick={() =>
              setRefreshVersion(
                (current) => current + 1
              )
            }
          >
            ↻ Actualizar
          </button>
        </div>

        <label className="inventory-warehouse-selector">
          <span>Bodega consultada</span>

          <select
            value={selectedWarehouseId}
            onChange={handleWarehouseChange}
          >
            <option value="">
              Todas las bodegas
            </option>

            {warehouses.map((warehouse) => (
              <option
                key={warehouse.id}
                value={warehouse.id}
              >
                {warehouse.name}
                {warehouse.is_default
                  ? " · Principal"
                  : ""}
              </option>
            ))}
          </select>
        </label>
      </div>

      {Object.entries(errors).map(
        ([key, message]) =>
          message && (
            <div
              key={key}
              className="alert alert-warning"
              role="alert"
            >
              {message}
            </div>
          )
      )}

      <section
        className="inventory-summary-grid"
        aria-label="Resumen del inventario"
      >
        <article className="inventory-summary-card stock-total">
          <span>Productos encontrados</span>
          <strong>{inventoryTotal}</strong>
          <small>
            Según los filtros aplicados
          </small>
        </article>

        <button
          type="button"
          className="inventory-summary-card alert-exhausted"
          onClick={showStockAlerts}
        >
          <span>Agotados</span>
          <strong>
            {alertSummary.exhausted}
          </strong>
          <small>
            Stock en cero o negativo
          </small>
        </button>

        <button
          type="button"
          className="inventory-summary-card alert-critical"
          onClick={showStockAlerts}
        >
          <span>Stock crítico</span>
          <strong>
            {alertSummary.critical}
          </strong>
          <small>
            Reposición prioritaria
          </small>
        </button>

        <button
          type="button"
          className="inventory-summary-card low-stock"
          onClick={showStockAlerts}
        >
          <span>Stock bajo</span>
          <strong>
            {alertSummary.low}
          </strong>
          <small>
            {alertSummary.total_alerts} alertas activas
          </small>
        </button>

        <button
          type="button"
          className="inventory-summary-card alert-expired"
          onClick={() =>
            showLotAlerts("expired")
          }
        >
          <span>Lotes vencidos</span>
          <strong>
            {alertSummary.expired}
          </strong>
          <small>
            No deben venderse
          </small>
        </button>

        <button
          type="button"
          className="inventory-summary-card alert-expiring"
          onClick={() =>
            showLotAlerts("expiring_soon")
          }
        >
          <span>Próximos a vencer</span>
          <strong>
            {alertSummary.expiring_soon}
          </strong>
          <small>
            Según el período de alerta configurado
          </small>
        </button>
      </section>

      {/* Carga de existencias desde Excel. */}
      <InventoryExcelPOS
        key={company}
        onCompleted={refreshInventory}
      />

      {/* Registro manual de movimientos. */}
      <section className="inventory-card movement-form-card">
        <h2>Registrar movimiento</h2>

        <p>
          Registra entradas, salidas y ajustes manuales.
          Las compras y ventas registradas en sus
          módulos no deben ingresarse otra vez aquí.
        </p>

        <form onSubmit={createMovement}>
          <fieldset
            disabled={saving}
            style={{
              border: 0,
              padding: 0,
              margin: 0,
              minWidth: 0,
            }}
          >
            <div className="inventory-form-grid">
              <div className="inventory-field">
                <label htmlFor="movement-warehouse">
                  Bodega
                </label>

                <select
                  id="movement-warehouse"
                  name="warehouse_id"
                  value={movementData.warehouse_id}
                  onChange={handleMovementChange}
                  required
                >
                  <option value="">
                    Selecciona una bodega
                  </option>

                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.id}
                      value={warehouse.id}
                    >
                      {warehouse.name}
                      {warehouse.is_default
                        ? " · Principal"
                        : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="inventory-field inventory-field-wide">
                <label htmlFor="movement-product">
                  Producto
                </label>

                <select
                  id="movement-product"
                  name="product_id"
                  value={movementData.product_id}
                  onChange={handleMovementChange}
                  required
                >
                  <option value="">
                    Selecciona un producto
                  </option>

                  {productOptions
                    .filter(
                      (product) => product.is_active
                    )
                    .map((product) => (
                      <option
                        key={product.id}
                        value={product.id}
                      >
                        {product.sku} - {product.name}
                      </option>
                    ))}
                </select>

                {selectedProduct && (
                  <small className="selected-stock">
                    Producto:{" "}
                    <strong>
                      {selectedProduct.name}
                    </strong>
                  </small>
                )}
              </div>

              {movementData.product_id && (
                <div className="inventory-field inventory-field-wide">
                  <label htmlFor="movement-variant">
                    Variante
                  </label>

                  <select
                    id="movement-variant"
                    name="variant_id"
                    value={movementData.variant_id}
                    onChange={handleMovementChange}
                    disabled={
                      loadingProductVariants ||
                      Boolean(variantError)
                    }
                  >
                    <option value="">
                      {loadingProductVariants
                        ? "Cargando variantes..."
                        : "Producto base (sin variante)"}
                    </option>

                    {productVariants.map((variant) => (
                      <option
                        key={variant.id}
                        value={variant.id}
                      >
                        {variant.sku} - {variant.name}
                        {variant.sale_price != null
                          ? ` · ${formatMoney(
                              variant.sale_price
                            )}`
                          : ""}
                      </option>
                    ))}
                  </select>

                  {variantError ? (
                    <small role="alert">
                      {variantError}
                    </small>
                  ) : selectedVariant ? (
                    <small className="selected-stock">
                      Variante:{" "}
                      <strong>
                        {selectedVariant.name}
                      </strong>
                    </small>
                  ) : !loadingProductVariants ? (
                    <small className="selected-stock">
                      Si no seleccionas una variante,
                      el movimiento afectará al
                      producto base.
                    </small>
                  ) : null}
                </div>
              )}

              <div className="inventory-field">
                <label htmlFor="movement-type">
                  Tipo
                </label>

                <select
                  id="movement-type"
                  name="movement_type"
                  value={movementData.movement_type}
                  onChange={handleMovementChange}
                >
                  <option value="entry">
                    Entrada
                  </option>
                  <option value="exit">
                    Salida
                  </option>
                </select>
              </div>

              <div className="inventory-field">
                <label htmlFor="movement-quantity">
                  Cantidad
                </label>

                <input
                  id="movement-quantity"
                  type="number"
                  name="quantity"
                  min="0.001"
                  step="0.001"
                  value={movementData.quantity}
                  onChange={handleMovementChange}
                  placeholder="Ejemplo: 10"
                  required
                />
              </div>

              <div className="inventory-field inventory-field-wide">
                <label htmlFor="movement-concept">
                  Concepto
                </label>

                <input
                  id="movement-concept"
                  name="concept"
                  minLength={3}
                  maxLength={255}
                  placeholder="Ejemplo: Ajuste por conteo físico"
                  value={movementData.concept}
                  onChange={handleMovementChange}
                  required
                />
              </div>

              {movementData.movement_type === "entry" && (
                <>
                  <div className="inventory-field">
                    <label htmlFor="movement-lot">
                      Número de lote
                    </label>

                    <input
                      id="movement-lot"
                      name="lot_number"
                      maxLength={100}
                      value={movementData.lot_number}
                      onChange={handleMovementChange}
                      placeholder="Ejemplo: L-2026-001"
                    />
                  </div>

                  <div className="inventory-field">
                    <label htmlFor="movement-expiration">
                      Fecha de vencimiento
                    </label>

                    <input
                      id="movement-expiration"
                      type="date"
                      name="expiration_date"
                      value={
                        movementData.expiration_date
                      }
                      onChange={handleMovementChange}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="inventory-field">
              <label htmlFor="movement-note">
                Nota adicional
              </label>

              <textarea
                id="movement-note"
                name="note"
                rows={3}
                placeholder="Información adicional (opcional)"
                value={movementData.note}
                onChange={handleMovementChange}
              />
            </div>

            <button
              type="submit"
              className={
                movementData.movement_type === "entry"
                  ? "btn-register-entry"
                  : "btn-register-exit"
              }
              disabled={
                saving ||
                loadingProductVariants ||
                Boolean(variantError) ||
                Boolean(errors.warehouses) ||
                Boolean(errors.products)
              }
            >
              {saving
                ? "Registrando..."
                : movementData.movement_type === "entry"
                  ? "Registrar entrada"
                  : "Registrar salida"}
            </button>
          </fieldset>
        </form>
      </section>

      {/* Existencias actuales. */}
      <section
        id="inventory-stock"
        className="inventory-card stock-list-card"
      >
        <div className="section-title">
          <div>
            <h2>Stock actual por producto</h2>
            <p>
              Busca por nombre o SKU y consulta las alertas.
            </p>
          </div>

          <span className="stock-alert-counter">
            {alertSummary.total_alerts} alertas activas
          </span>
        </div>

        <div className="inventory-filters">
          <input
            type="search"
            aria-label="Buscar producto o SKU"
            placeholder="Buscar producto o SKU"
            value={inventorySearch}
            onChange={(event) => {
              setInventorySearch(
                event.target.value
              );
              setInventoryOffset(0);
            }}
          />

          <label className="low-stock-filter">
            <input
              type="checkbox"
              checked={onlyLowStock}
              onChange={(event) => {
                setOnlyLowStock(
                  event.target.checked
                );
                setInventoryOffset(0);
              }}
            />
            Solo productos con alerta
          </label>
        </div>

        {loading && (
          <p role="status">
            Actualizando existencias…
          </p>
        )}

        <div className="inventory-table-wrapper">
          <table
            className="inventory-table"
            aria-busy={loading}
          >
            <thead>
              <tr>
                <th>Código</th>
                <th>Producto</th>
                <th>Categoría</th>
                <th>Stock actual</th>
                <th>Stock mínimo</th>
                <th>Faltante</th>
                <th>Estado</th>
              </tr>
            </thead>

            <tbody>
              {inventory.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="empty-inventory"
                  >
                    {errors.inventory
                      ? "No se pudo consultar el inventario."
                      : loading
                        ? "Cargando..."
                        : "No hay productos para estos filtros."}
                  </td>
                </tr>
              ) : (
                inventory.map((product, index) => (
                  <tr
                    key={`${product.id}:${
                      product.variant_id ?? "base"
                    }:${index}`}
                    className={
                      product.alert_status
                        ? "low-stock-row"
                        : ""
                    }
                  >
                    <td>{product.sku}</td>

                    <td>{product.name}</td>

                    <td>
                      {product.category ||
                        "Sin categoría"}
                    </td>

                    <td>
                      <strong
                        className={
                          product.alert_status
                            ? "stock-low"
                            : "stock-normal"
                        }
                      >
                        {formatQuantity(
                          product.current_stock
                        )}
                      </strong>
                    </td>

                    <td>
                      {formatQuantity(
                        product.minimum_stock
                      )}
                    </td>

                    <td>
                      {Number(
                        product.units_to_minimum
                      ) > 0
                        ? formatQuantity(
                            product.units_to_minimum
                          )
                        : "—"}
                    </td>

                    <td>
                      <span
                        className={
                          {
                            exhausted:
                              "badge-stock-exhausted",
                            critical:
                              "badge-stock-critical",
                            low:
                              "badge-low-stock",
                          }[product.alert_status] ||
                          "badge-stock-ok"
                        }
                      >
                        {{
                          exhausted: "Agotado",
                          critical: "Crítico",
                          low: "Stock bajo",
                        }[product.alert_status] ||
                          "Disponible"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          total={inventoryTotal}
          offset={inventoryOffset}
          onPageChange={setInventoryOffset}
        />
      </section>

      {/* Lotes y vencimientos. */}
      <section
        id="inventory-lots"
        className="inventory-card inventory-lots-card"
      >
        <div className="section-title">
          <div>
            <h2>Lotes disponibles y vencimientos</h2>
            <p>
              Consulta saldos y fechas de vencimiento.
            </p>
          </div>

          <span className="stock-alert-counter">
            {lotsTotal} lotes encontrados
          </span>
        </div>

        <div className="inventory-filters">
          <select
            aria-label="Filtrar lotes por vencimiento"
            value={lotExpirationFilter}
            onChange={(event) => {
              setLotExpirationFilter(
                event.target.value
              );
              setLotsOffset(0);
            }}
          >
            <option value="">
              Todos los lotes
            </option>

            <option value="expired">
              Vencidos
            </option>

            <option value="expiring_soon">
              Próximos a vencer
            </option>

            <option value="valid">
              Vigentes
            </option>

            <option value="without_expiration">
              Sin vencimiento
            </option>
          </select>

          {lotExpirationFilter && (
            <button
              type="button"
              className="btn-clear-filters"
              onClick={() => {
                setLotExpirationFilter("");
                setLotsOffset(0);
              }}
            >
              Limpiar filtro
            </button>
          )}
        </div>

        <div className="inventory-table-wrapper">
          <table className="inventory-table inventory-lots-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Bodega</th>
                <th>Lote</th>
                <th>Vencimiento</th>
                <th>Días</th>
                <th>Disponible</th>
                <th>Costo unitario</th>
                <th>Estado</th>
              </tr>
            </thead>

            <tbody>
              {lots.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="empty-inventory"
                  >
                    {errors.lots
                      ? "No se pudieron consultar los lotes."
                      : "No hay lotes para estos filtros."}
                  </td>
                </tr>
              ) : (
                lots.map((lot) => (
                  <tr
                    key={lot.id}
                    className={
                      lot.expiration_status === "expired"
                        ? "lot-expired-row"
                        : lot.expiration_status ===
                            "expiring_soon"
                          ? "lot-expiring-row"
                          : ""
                    }
                  >
                    <td>
                      <strong>
                        {lot.product_name}
                      </strong>

                      <br />

                      <small>
                        {lot.product_sku}
                      </small>
                    </td>

                    <td>
                      {lot.warehouse_name ||
                        "Sin bodega"}
                    </td>

                    <td>
                      {lot.lot_number || "—"}
                    </td>

                    <td>
                      {formatDate(
                        lot.expiration_date
                      )}
                    </td>

                    <td>
                      {lot.days_to_expiration == null
                        ? "—"
                        : lot.days_to_expiration < 0
                          ? `${Math.abs(
                              lot.days_to_expiration
                            )} vencidos`
                          : `${lot.days_to_expiration} días`}
                    </td>

                    <td>
                      {formatQuantity(
                        lot.available_quantity
                      )}
                    </td>

                    <td>
                      {formatMoney(
                        lot.unit_cost
                      )}
                    </td>

                    <td>
                      <span
                        className={`lot-status ${
                          lot.expiration_status || ""
                        }`}
                      >
                        {lotStatusLabel(
                          lot.expiration_status
                        )}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          total={lotsTotal}
          offset={lotsOffset}
          onPageChange={setLotsOffset}
        />
      </section>

      {/* Historial de entradas y salidas. */}
      <section className="inventory-card movements-history-card">
        <div className="section-title">
          <div>
            <h2>Historial de movimientos</h2>
            <p>
              Filtra por producto, tipo o fechas.
            </p>
          </div>
        </div>

        <div className="inventory-movement-filters">
          <input
            type="search"
            aria-label="Buscar movimientos"
            placeholder="Buscar producto o SKU"
            value={movementSearch}
            onChange={(event) => {
              setMovementSearch(
                event.target.value
              );
              setMovementsOffset(0);
            }}
          />

          <select
            aria-label="Filtrar movimientos por producto"
            value={movementProductFilter}
            onChange={(event) => {
              setMovementProductFilter(
                event.target.value
              );
              setMovementsOffset(0);
            }}
          >
            <option value="">
              Todos los productos
            </option>

            {productOptions.map((product) => (
              <option
                key={product.id}
                value={product.id}
              >
                {product.sku} - {product.name}
              </option>
            ))}
          </select>

          <select
            aria-label="Tipo de movimiento"
            value={movementTypeFilter}
            onChange={(event) => {
              setMovementTypeFilter(
                event.target.value
              );
              setMovementsOffset(0);
            }}
          >
            <option value="">
              Entradas y salidas
            </option>

            <option value="entry">
              Entradas
            </option>

            <option value="exit">
              Salidas
            </option>
          </select>

          <input
            type="date"
            aria-label="Desde"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(
                event.target.value
              );
              setMovementsOffset(0);
            }}
          />

          <input
            type="date"
            aria-label="Hasta"
            value={dateTo}
            onChange={(event) => {
              setDateTo(
                event.target.value
              );
              setMovementsOffset(0);
            }}
          />

          <button
            type="button"
            className="btn-clear-filters"
            onClick={resetMovementFilters}
          >
            Limpiar filtros
          </button>
        </div>

        <div className="inventory-table-wrapper">
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Producto</th>
                <th>Bodega</th>
                <th>Tipo</th>
                <th>Concepto</th>
                <th>Cantidad</th>
              </tr>
            </thead>

            <tbody>
              {movements.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="empty-inventory"
                  >
                    {errors.movements
                      ? "No se pudieron consultar los movimientos."
                      : "No hay movimientos para estos filtros."}
                  </td>
                </tr>
              ) : (
                movements.map((movement) => (
                  <tr key={movement.id}>
                    <td>
                      {formatDateTime(
                        movement.created_at
                      )}
                    </td>

                    <td>
                      {movement.variant_sku ? (
                        <>
                          <strong>
                            {movement.variant_sku}
                          </strong>

                          <br />

                          <small>
                            {movement.product_name ||
                              productNames[
                                movement.product_id
                              ] ||
                              "Producto"}{" "}
                            ·{" "}
                            {movement.variant_name ||
                              "Variante"}
                          </small>
                        </>
                      ) : (
                        movement.product_name ||
                        productNames[
                          movement.product_id
                        ] ||
                        `Producto #${movement.product_id}`
                      )}
                    </td>

                    <td>
                      {movement.warehouse_name ||
                        "Sin bodega"}
                    </td>

                    <td>
                      <span
                        className={
                          movement.movement_type ===
                          "entry"
                            ? "movement-entry"
                            : "movement-exit"
                        }
                      >
                        {movement.movement_type ===
                        "entry"
                          ? "Entrada"
                          : "Salida"}
                      </span>
                    </td>

                    <td>
                      {movement.concept}
                    </td>

                    <td
                      className={
                        movement.movement_type ===
                        "entry"
                          ? "quantity-entry"
                          : "quantity-exit"
                      }
                    >
                      {movement.movement_type ===
                      "entry"
                        ? "+"
                        : "-"}

                      {formatQuantity(
                        movement.quantity
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          total={movementsTotal}
          offset={movementsOffset}
          onPageChange={setMovementsOffset}
        />
      </section>
    </div>
  );
}